// Kampf am Ufer (§6): eigenes Verhalten der neuen Sturmwesen (Runde 7) und die Zielwahl.
// Aufgeteilt aus battle.ts.
import {
  getEnemy,
  systemOn,
} from '../../content/index.ts';
import type {
  Unit,
} from '../types.ts';
import type { Battle } from './core.ts';
import {
  EPS,
  unitById,
  dist,
  gap,
  round,
  emit,
  targetable,
  enemiesOf,
  nearest,
  setStatus,
  nextRetarget,
  moveToward,
  clampToField,
  shove,
  gainMut,
  attack,
  placeNextTo,
  dealDamage,
  hitDamage,
  param,
} from './core.ts';
import {
  fireKniff,
  hindmostFoe,
} from './kniffe.ts';

// ---------- Neue Sturmwesen (Runde 7, Fabi) ----------

/**
 * G26 Nieselchen (Fabi, Phase Kern, 17.10.2026): Es versteckt sich hinter einem stärkeren Sturmwesen (eines mit mehr
 * Leben) und wirft von dort Blätter. Mit seinem Kniff springt es zum Freund in der Nähe, der am meisten Schaden gemacht
 * hat, schlägt zweimal schnell mit dem Blattschwert zu und versteckt sich danach schnell wieder. Ist es das letzte
 * Sturmwesen oder gibt es keines mit mehr Leben, bleibt es beim Blattschwert und kämpft aus der Nähe, etwas stärker.
 */
export function updateHider(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'hider' || !u.hide) return false;
  const p = u.pounce;
  if (p) {
    if (p.lingerUntil !== undefined) {
      if (b.t + EPS < p.lingerUntil) return true;
      u.pounce = null;
      const guard = protectorOf(b, u);
      if (guard) {
        // schnell zurück ins Versteck
        const spot = hideSpot(b, u, guard);
        u.x = spot.x;
        u.y = spot.y;
        clampToField(b, u);
        u.targetUid = null;
        emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'return' });
      }
      return true;
    }
    if (b.t + EPS < p.nextHitAt) return true;
    const victim = unitById(b, p.targetUid);
    if (victim && targetable(b, victim) && p.hitsLeft > 0) {
      dealDamage(b, u, victim, hitDamage(b, u, param(u, u.kniff!, 'damageFactor')), 'kniff');
      p.hitsLeft -= 1;
    }
    if (!u.pounce) return true; // der Hieb kann es selbst zu Fall bringen
    if (!victim || !victim.alive || p.hitsLeft <= 0) p.lingerUntil = b.t + param(u, u.kniff!, 'lingerSeconds');
    else p.nextHitAt = b.t + param(u, u.kniff!, 'hitInterval');
    return true;
  }
  if (taunted(u)) return false;
  const guard = protectorOf(b, u);
  u.hideMelee = !guard;
  u.range = guard ? u.hide.range : u.hide.meleeRange;
  if (u.mut >= u.mutMax - EPS && fireKniff(b, u)) return true;
  if (!guard) return false; // ohne Versteck wie alle anderen, aus der Nähe
  const spot = hideSpot(b, u, guard);
  const inReach = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree' && gap(u, o) <= u.range + EPS);
  const tgt = nearest(u, inReach);
  u.targetUid = tgt?.uid ?? null;
  if (dist(u, spot) > 1.5) moveToward(b, u, spot.x, spot.y);
  if (tgt && u.attackCooldown <= EPS) {
    attack(b, u, tgt);
    u.attackCooldown += u.attackInterval;
  }
  return true;
}

/** G26: das nächste Sturmwesen mit mehr Leben, hinter dem es sich verstecken kann */
function protectorOf(b: Battle, u: Unit): Unit | undefined {
  return nearest(u, b.units.filter((o) => o.side === u.side && o !== u && o.alive && o.kind !== 'tree' && o.hp > u.hp + EPS));
}

/** G26: der Platz direkt hinter dem Beschützer, von den Freunden aus gesehen */
function hideSpot(b: Battle, u: Unit, guard: Unit): { x: number; y: number } {
  const foe = nearest(guard, b.units.filter((o) => o.side !== u.side && o.alive && o.kind !== 'tree'));
  let dx = foe ? guard.x - foe.x : u.side === 'enemy' ? 1 : -1;
  let dy = foe ? guard.y - foe.y : 0;
  const d = Math.hypot(dx, dy) || 1;
  dx /= d;
  dy /= d;
  const back = guard.radius + u.radius + 1;
  const r = u.radius;
  return {
    x: Math.min(b.rules.field.width - r, Math.max(r, guard.x + dx * back)),
    y: Math.min(b.rules.field.height - r, Math.max(r, guard.y + dy * back)),
  };
}

/** Wer verspottet ist (Igelburg, Stachelkugel), folgt dem Spott statt seinem eigenen Verhalten */
export const taunted = (u: Unit) => u.tauntBy !== null && u.tauntAttacks > 0;

/** G22 Dreckschleuder: bewirft die eigenen Schwächsten mit schützendem Dreck, statt anzugreifen */
export function updateSupport(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'support' || taunted(u)) return false;
  if (u.mut >= u.mutMax - EPS && fireKniff(b, u)) return true;
  const allies = b.units.filter((o) => o.side === u.side && o !== u && o.alive && o.kind !== 'tree');
  if (!allies.length) return false; // allein wehrt sie sich wie alle anderen
  let ally = unitById(b, u.targetUid);
  if (!ally || ally.side !== u.side || !ally.alive || ally === u) {
    // einer der beiden Schwächsten, zufällig
    const weakest = [...allies].sort((a, c) => a.hp / a.maxHp - c.hp / c.maxHp || a.uid - c.uid).slice(0, 2);
    ally = weakest[Math.floor(b.rng.next() * weakest.length)]!;
    u.targetUid = ally.uid;
  }
  if (gap(u, ally) > u.range) {
    moveToward(b, u, ally.x, ally.y);
    return true;
  }
  if (u.attackCooldown <= EPS) {
    emit(b, { t: round(b.t), type: 'strike', unit: u.uid, target: ally.uid, kind: 'shield' });
    const amount = u.damage;
    ally.shield = Math.min(ally.shield + amount, amount * 2);
    ally.shieldSource = u.uid;
    setStatus(b, ally, 'shielded', b.t, 'behavior:support');
    gainMut(b, u, u.mutPerAttack);
    u.attackCooldown += u.attackInterval;
    u.targetUid = null;
  }
  return true;
}

/** G23 Blitzflitzer: huscht zu einem zufälligen Freund, schlägt ein- bis viermal zu und huscht zum nächsten */
export function updateFlitter(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'flitter' || taunted(u)) return false;
  if (u.mut >= u.mutMax - EPS && fireKniff(b, u)) return true;
  const foes = b.units.filter((o) => o.side !== u.side && targetable(b, o) && o.kind !== 'tree');
  if (!foes.length) return false;
  let tgt = u.flit ? unitById(b, u.flit.targetUid) : undefined;
  if (!u.flit || !tgt || !targetable(b, tgt) || u.flit.hitsLeft <= 0) {
    const others = foes.filter((o) => o.uid !== u.flit?.targetUid);
    const pool = others.length ? others : foes;
    tgt = pool[Math.floor(b.rng.next() * pool.length)]!;
    const cfg = getEnemy(u.defId).flitter ?? { min: 1, max: 4 };
    u.flit = { targetUid: tgt.uid, hitsLeft: cfg.min + Math.floor(b.rng.next() * (cfg.max - cfg.min + 1)) };
  }
  u.targetUid = tgt.uid;
  if (gap(u, tgt) > u.range) {
    moveToward(b, u, tgt.x, tgt.y);
    return true;
  }
  if (u.attackCooldown <= EPS) {
    attack(b, u, tgt);
    u.attackCooldown += u.attackInterval;
    u.flit.hitsLeft -= 1;
  }
  return true;
}

/** G24 Donnergroller: zieht unaufhörlich und langsam zur größten Gruppe Freunde und schlägt, wer in Reichweite ist */
export function updateCrowd(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'crowd' || taunted(u)) return false;
  if (u.mut >= u.mutMax - EPS && fireKniff(b, u)) return true;
  const foes = b.units.filter((o) => o.side !== u.side && targetable(b, o));
  if (!foes.length) return false;
  const R = 20;
  let center = foes[0]!;
  let most = -1;
  for (const o of foes) {
    const n = foes.filter((q) => dist(q, o) <= R).length;
    if (n > most || (n === most && dist(u, o) < dist(u, center))) {
      center = o;
      most = n;
    }
  }
  const group = foes.filter((q) => dist(q, center) <= R);
  const cx = group.reduce((a, q) => a + q.x, 0) / group.length;
  const cy = group.reduce((a, q) => a + q.y, 0) / group.length;
  const inReach = foes.filter((o) => gap(u, o) <= u.range + EPS);
  const tgt = nearest(u, inReach.length ? inReach : foes);
  u.targetUid = tgt?.uid ?? null;
  if (Math.hypot(cx - u.x, cy - u.y) > u.radius) moveToward(b, u, cx, cy);
  if (tgt && gap(u, tgt) <= u.range + EPS && u.attackCooldown <= EPS) {
    attack(b, u, tgt);
    u.attackCooldown += u.attackInterval;
  }
  return true;
}

/**
 * G25 Prallgraupel: wirft sich gegen einen Freund, stößt ihn ein winziges Stück zurück und landet wieder auf Abstand.
 * Er greift erst wieder an, wenn er gelandet ist; so hält er Nahkämpfer hin.
 */
export function updateBounce(b: Battle, u: Unit): boolean {
  if (u.behavior !== 'bounce' || taunted(u)) return false;
  if (u.mut >= u.mutMax - EPS && fireKniff(b, u)) return true;
  const tgt = chooseEnemyTarget(b, u);
  u.targetUid = tgt?.uid ?? null;
  if (!tgt) return true;
  if (gap(u, tgt) > u.range) {
    moveToward(b, u, tgt.x, tgt.y);
    return true;
  }
  if (!targetable(b, tgt) || u.attackCooldown > EPS) return true;
  placeNextTo(b, u, tgt);
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'leap' });
  attack(b, u, tgt);
  u.attackCooldown += u.attackInterval;
  const d = Math.max(dist(u, tgt), EPS);
  const nx = (u.x - tgt.x) / d;
  const ny = (u.y - tgt.y) / d;
  if (tgt.alive && tgt.kind !== 'tree' && !tgt.onTree) {
    // Spieltest Phase 4 (Fabi): Leichte Tiere fliegen weiter; Q62: gemessen an einem Dachs
    const push = 3 * (b.rules.edge.pushRadius / tgt.radius) ** 2;
    shove(b, tgt, -nx * push, -ny * push);
  }
  const r = tgt.radius + u.radius + u.range;
  u.x = tgt.x + nx * r;
  u.y = tgt.y + ny * r;
  clampToField(b, u);
  emit(b, { t: round(b.t), type: 'move', unit: u.uid, x: round(u.x), y: round(u.y), reason: 'return' });
  return true;
}

// ---------- Zielwahl ----------

export function chooseEnemyTarget(b: Battle, u: Unit): Unit | undefined {
  const fanatic = u.traits.includes('fanatisch');
  const current = unitById(b, u.targetUid);
  // S5 Stachelkugel
  const taunter = unitById(b, u.tauntBy);
  if (taunter && u.tauntAttacks > 0 && targetable(b, taunter)) return taunter;
  // K23 Fanatisch: wechselt nie, wartet notfalls vor dem eingerollten Igel
  if (fanatic && current && current.alive && !current.onTree) return current;
  // R27 Erdüberraschung: auf einen eingegrabenen Dachs warten alle, die ihn schon im Blick hatten
  if (current && current.alive && current.burrowUntil > b.t + EPS) return current;
  const candidates = b.units.filter((o) => o.side !== u.side && targetable(b, o));
  if (candidates.length === 0) return undefined;
  // K23 Unruhig: wechselt alle paar Sekunden auf ein zufälliges Ziel
  if (u.traits.includes('unruhig') && b.t + EPS >= u.retargetAt) {
    u.retargetAt = nextRetarget(b);
    return candidates[Math.floor(b.rng.next() * candidates.length)];
  }
  // K21 (Fabi, Spieltest Phase 4): Kämpfen soll natürlicher wirken. Wird das Ziel weggeschubst oder steht plötzlich
  // ein leichteres Ziel direkt vor der Nase, wechselt ein Gegner, der auf den Nächsten geht, ab und zu den Fokus
  if (current && targetable(b, current) && u.behavior === 'nearest' && current.uid === u.engagedUid && b.t + EPS >= u.switchAt) {
    u.switchAt = b.t + b.rules.edge.switchEvery;
    const near = nearest(u, candidates);
    if (near && near !== current && gap(u, near) + b.rules.edge.switchMargin < gap(u, current)) {
      u.engagedUid = null;
      return near;
    }
    // K36 (Fabi, Phase Kern): Der Wechsel aufs schwächere Ziel ist ausgeblendet; gewechselt wird nur noch bei Nähe
    const weak = systemOn('weakTargetSwitch') ? candidates.filter((o) => o !== current && o.kind !== 'tree' && gap(u, o) <= u.range + 1 + EPS).sort((a, c) => a.hp - c.hp)[0] : undefined;
    if (weak && weak.hp < current.hp * 0.5) {
      u.engagedUid = null;
      return weak;
    }
  }
  // K27 (Fabi, Spieltest Phase 4): Wer noch niemanden angegriffen hat, nimmt den Nächsten, auch einen Freund, der
  // gerade vorbeimarschiert. Wer schon kämpft, bleibt dran; so kann man jemanden an einem beschäftigten Gegner vorbeischicken
  if (current && targetable(b, current) && (u.behavior !== 'nearest' || current.uid === u.engagedUid || u.traits.includes('unruhig'))) return current;
  if (u.behavior === 'hindmost') return hindmostFoe(b, u) ?? nearest(u, candidates);
  return nearest(u, candidates);
}

export function chooseFriendTarget(b: Battle, u: Unit): Unit | undefined {
  // S8 Diebische Elster (Runde 7): Was glänzt, zieht sie an; solange ein Sturmwesen ein Item trägt, geht sie zuerst dorthin
  if (u.passive === 'diebische-elster' && !(u.tauntBy !== null && u.tauntAttacks > 0)) {
    const shiny = enemiesOf(b, u).filter((o) => o.items.length > 0);
    if (shiny.length) return nearest(u, shiny);
  }
  // Ein befallener Igel kann auch Freunde an sich binden
  const taunter = unitById(b, u.tauntBy);
  if (taunter && u.tauntAttacks > 0 && targetable(b, taunter)) return taunter;
  // G18 (Fabi, Spieltest Phase 4): Wer schon angreift, bleibt dran. Wer noch unterwegs ist, nimmt den Nächsten,
  // auch einen Windschnitt, der gerade hinten hereinspringt
  const current = unitById(b, u.targetUid);
  if (current && current.uid === u.engagedUid && current.side !== u.side && targetable(b, current)) return current;
  return nearest(u, enemiesOf(b, u));
}
